import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  getGoogleCalendarSettings,
  removeGoogleCalendarLink,
  saveGoogleCalendarId,
  verifyGoogleCalendar,
  type GoogleCalendarSettingsDto,
} from '../lib/google-calendar-api.js';

/**
 * Krok GCAL1 bod 1 — Nastavení → Integrace → „Google kalendář".
 *
 * Vzhled bez nových komponent (`rh-card`, `rh-a2-link`) — stejné jako
 * `KatastrSettingsCard` ve stejné záložce.
 *
 * Nastavuje si ho KAŽDÝ MAKLÉŘ SÁM (ne jen owner/admin): je to jeho
 * kalendář, ne nastavení kanceláře — tentýž důvod, proč má server právo
 * `crm.calendar_feed.manage` (odběr kalendáře) a ne `identity.tenant.update`.
 */

/**
 * Klíč hlášky ze serveru se přeloží jen tehdy, když je OPRAVDU z katalogu.
 * Cizí řetězec by jinak skončil v UI jako „crm.googleCalendar.error.cosi"
 * (nebo by shodil stránku — poučení S17i).
 */
const GCAL_ERROR_KEYS = [
  'crm.googleCalendar.error.disabled',
  'crm.googleCalendar.error.notShared',
  'crm.googleCalendar.error.notFound',
  'crm.googleCalendar.error.credentials',
  'crm.googleCalendar.error.limit',
  'crm.googleCalendar.error.badRequest',
  'crm.googleCalendar.error.server',
  'crm.googleCalendar.error.network',
  'crm.googleCalendar.error.noCalendar',
] as const;

type GcalErrorKey = (typeof GCAL_ERROR_KEYS)[number];

export function gcalErrorKey(value: string | null): GcalErrorKey {
  return GCAL_ERROR_KEYS.find((key) => key === value) ?? 'crm.googleCalendar.error.server';
}

export function GoogleCalendarCard(): React.JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const state = useQuery<GoogleCalendarSettingsDto>({
    queryKey: ['nastaveni', 'google-calendar'],
    queryFn: getGoogleCalendarSettings,
  });
  const [draft, setDraft] = React.useState('');
  const [note, setNote] = React.useState<string | null>(null);
  const [copyNote, setCopyNote] = React.useState<string | null>(null);

  const settings = state.data;
  const calendarId = settings?.calendarId ?? null;
  const serviceAccountEmail = settings?.serviceAccountEmail ?? null;

  /*
   * Rozepsané pole se plní ze serveru v RENDERU, ne v `useEffect` — poučení
   * z nálezu v `contracts.spec` (závod efektu po připojení dělá falešnou
   * červenou pod plnou bránou). Klíč je ID kalendáře: když se změní (jiná
   * odpověď serveru), `useState` se reinicializuje.
   */
  const [loadedFor, setLoadedFor] = React.useState<string | null>(null);
  if (calendarId !== null && loadedFor !== calendarId) {
    setLoadedFor(calendarId);
    setDraft(calendarId);
  }

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'google-calendar'] });
  }

  async function onSave(): Promise<void> {
    setNote(null);
    try {
      await saveGoogleCalendarId(draft.trim());
      await refresh();
      setNote(t('webAdmin.settings.gcalSaved'));
    } catch (e) {
      setNote(t('webAdmin.settings.gcalSaveError', { error: errorMessage(e) }));
    }
  }

  async function onVerify(): Promise<void> {
    setNote(null);
    try {
      const result = await verifyGoogleCalendar();
      await refresh();
      setNote(result.ok ? t('webAdmin.settings.gcalSaved') : t(gcalErrorKey(result.messageKey)));
    } catch (e) {
      setNote(t('webAdmin.settings.gcalSaveError', { error: errorMessage(e) }));
    }
  }

  async function onRemove(): Promise<void> {
    setNote(null);
    try {
      await removeGoogleCalendarLink();
      setDraft('');
      setLoadedFor(null);
      await refresh();
      setNote(t('webAdmin.settings.gcalRemoved'));
    } catch (e) {
      setNote(t('webAdmin.settings.gcalSaveError', { error: errorMessage(e) }));
    }
  }

  async function onCopy(): Promise<void> {
    if (serviceAccountEmail === null) return;
    try {
      await navigator.clipboard.writeText(serviceAccountEmail);
      setCopyNote(t('webAdmin.settings.gcalCopied'));
    } catch {
      // clipboard bez povoleni — adresu si makler muze opsat rucne
    }
  }

  return (
    <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-gcal-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.gcalTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.gcalPerex')}</p>

      {/*
        Bez prihlasovacich udaju na serveru je funkce VYPNUTA (zadani bod 4).
        Karta se presto kresli — makler ma vedet, ze funkce existuje a ceka
        na nastaveni, misto aby ji nenasel.
      */}
      {settings !== undefined && !settings.available ? (
        <p className="mb-2 text-sm" data-testid="gcal-disabled">
          {t('webAdmin.settings.gcalDisabled')}
        </p>
      ) : null}

      {serviceAccountEmail !== null ? (
        <div className="mb-3">
          <span className="mb-1 block text-sm font-medium">{t('webAdmin.settings.gcalServiceAccountLabel')}</span>
          <div className="flex items-center gap-2">
            <code className="rounded bg-gray-100 px-2 py-1 text-xs" data-testid="gcal-service-account">
              {serviceAccountEmail}
            </code>
            <button type="button" className="rh-a2-link text-xs" data-testid="gcal-copy" onClick={() => void onCopy()}>
              {t('webAdmin.settings.gcalCopyButton')}
            </button>
            {copyNote !== null ? <span className="text-xs text-gray-600">{copyNote}</span> : null}
          </div>
          <p className="mt-1 text-xs text-gray-600" data-testid="gcal-share-howto">
            {t('webAdmin.settings.gcalShareHowTo', { email: serviceAccountEmail })}
          </p>
        </div>
      ) : null}

      <label className="mb-1 block text-sm font-medium" htmlFor="gcal-calendar-id">
        {t('webAdmin.settings.gcalCalendarIdLabel')}
      </label>
      <input
        id="gcal-calendar-id"
        type="text"
        autoComplete="off"
        className="mb-2 w-full rounded border px-2 py-1 text-sm"
        placeholder={t('webAdmin.settings.gcalCalendarIdPlaceholder')}
        value={draft}
        disabled={settings?.available === false}
        data-testid="gcal-calendar-id-input"
        onChange={(e) => setDraft(e.target.value)}
      />

      <div className="mb-2 flex items-center gap-3">
        <button
          type="button"
          className="rh-a2-link text-sm"
          disabled={settings?.available === false || draft.trim().length < 3}
          data-testid="gcal-save"
          onClick={() => void onSave()}
        >
          {t('webAdmin.settings.gcalSaveButton')}
        </button>
        <button
          type="button"
          className="rh-a2-link text-sm"
          disabled={settings?.available === false || calendarId === null}
          data-testid="gcal-verify"
          onClick={() => void onVerify()}
        >
          {t('webAdmin.settings.gcalVerifyButton')}
        </button>
        {calendarId !== null ? (
          <button type="button" className="rh-a2-link text-sm" data-testid="gcal-remove" onClick={() => void onRemove()}>
            {t('webAdmin.settings.gcalRemoveButton')}
          </button>
        ) : null}
      </div>

      {settings !== undefined && settings.verifiedAt !== null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="gcal-verified">
          {t('webAdmin.settings.gcalVerified', { date: settings.verifiedAt.slice(0, 10) })}
        </p>
      ) : calendarId !== null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="gcal-not-verified">
          {t('webAdmin.settings.gcalNotVerified')}
        </p>
      ) : null}

      {settings !== undefined && settings.lastError !== null ? (
        <p className="mb-2 text-xs" data-testid="gcal-last-error">
          {t(gcalErrorKey(settings.lastError))}
        </p>
      ) : null}

      {note !== null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="gcal-note">
          {note}
        </p>
      ) : null}

      <details className="text-sm">
        <summary className="cursor-pointer font-medium">{t('webAdmin.settings.gcalHowToTitle')}</summary>
        <p>{t('webAdmin.settings.gcalHowToStep1')}</p>
        <p>{t('webAdmin.settings.gcalHowToStep2')}</p>
        <p>{t('webAdmin.settings.gcalHowToStep3')}</p>
      </details>
    </div>
  );
}
